import { useLingui } from '@lingui/react/macro'
import type { FC, KeyboardEvent, PointerEvent } from 'react'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import {
  SyncedHorizontalScrollbar,
  SyncedHorizontalScrollbarThumb,
  SyncedHorizontalScrollbarTrack,
} from './TableStyles'

const TABLE_ROOT_SELECTOR = '.rt-TableRootTable'

type HorizontalScrollbarDragState = {
  maxScrollLeft: number
  maxThumbTravel: number
  pointerId: number
  startScrollLeft: number
  startX: number
}

type SyncedTableHorizontalScrollbarProps = {
  enabled?: boolean
  getViewport: () => HTMLElement | null | undefined
  isPinned?: boolean
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max)

const MIN_THUMB_WIDTH_PERCENT = 8
const MAX_THUMB_WIDTH_PERCENT = 24
const MIN_THUMB_WIDTH_PX = 32

const getThumbWidthPercent = (clientWidth: number, scrollWidth: number) => {
  if (scrollWidth <= clientWidth) {
    return 100
  }

  return clamp(
    (clientWidth / scrollWidth) * 100,
    MIN_THUMB_WIDTH_PERCENT,
    MAX_THUMB_WIDTH_PERCENT
  )
}

const useSyncedTableViewport = (
  enabled: boolean | undefined,
  getViewport: () => HTMLElement | null | undefined
) => {
  const [viewport, setViewport] = useState<HTMLElement | null>(null)

  useLayoutEffect(() => {
    const syncViewport = () => {
      const nextViewport = enabled ? (getViewport() ?? null) : null
      setViewport((previousViewport) =>
        previousViewport === nextViewport ? previousViewport : nextViewport
      )
    }

    if (!enabled) {
      syncViewport()
      return
    }

    let animationFrameId: number | null = null
    const scheduleSyncViewport = () => {
      if (typeof requestAnimationFrame === 'undefined') {
        syncViewport()
        return
      }

      if (animationFrameId !== null) {
        return
      }

      animationFrameId = requestAnimationFrame(() => {
        animationFrameId = null
        syncViewport()
      })
    }

    syncViewport()

    const cancelScheduledSync = () => {
      if (
        animationFrameId !== null &&
        typeof cancelAnimationFrame !== 'undefined'
      ) {
        cancelAnimationFrame(animationFrameId)
      }
    }

    if (
      typeof document === 'undefined' ||
      typeof MutationObserver === 'undefined' ||
      !document.body
    ) {
      return () => {
        cancelScheduledSync()
      }
    }

    const mutationObserver = new MutationObserver(scheduleSyncViewport)
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    })

    return () => {
      mutationObserver.disconnect()
      cancelScheduledSync()
    }
  }, [enabled, getViewport])

  return viewport
}

export const SyncedTableHorizontalScrollbar: FC<
  SyncedTableHorizontalScrollbarProps
> = ({ enabled, getViewport, isPinned = false }) => {
  const { t } = useLingui()
  const trackRef = useRef<HTMLDivElement | null>(null)
  const dragStateRef = useRef<HorizontalScrollbarDragState | null>(null)
  const viewport = useSyncedTableViewport(enabled, getViewport)
  const [metrics, setMetrics] = useState({
    clientWidth: 0,
    scrollLeft: 0,
    scrollWidth: 0,
  })

  useEffect(() => {
    if (!enabled || !viewport) {
      return
    }

    const updateMetrics = () => {
      setMetrics({
        clientWidth: viewport.clientWidth,
        scrollLeft: viewport.scrollLeft,
        scrollWidth: viewport.scrollWidth,
      })
    }

    updateMetrics()
    viewport.addEventListener('scroll', updateMetrics)

    const resizeObserver = new ResizeObserver(updateMetrics)
    resizeObserver.observe(viewport)

    const content = viewport.firstElementChild
    if (content instanceof HTMLElement) {
      resizeObserver.observe(content)
    }

    const table = viewport.querySelector(TABLE_ROOT_SELECTOR)
    if (table instanceof HTMLElement) {
      resizeObserver.observe(table)
    }

    return () => {
      viewport.removeEventListener('scroll', updateMetrics)
      resizeObserver.disconnect()
    }
  }, [enabled, viewport])

  const maxScrollLeft = Math.max(metrics.scrollWidth - metrics.clientWidth, 0)
  const isVisible = Boolean(enabled && viewport && maxScrollLeft > 0)
  const thumbWidthPercent =
    metrics.scrollWidth > 0
      ? getThumbWidthPercent(metrics.clientWidth, metrics.scrollWidth)
      : 100
  const thumbLeftPercent =
    maxScrollLeft > 0
      ? clamp(
          (metrics.scrollLeft / maxScrollLeft) * (100 - thumbWidthPercent),
          0,
          100 - thumbWidthPercent
        )
      : 0

  const scrollViewportToTrackPosition = useCallback(
    (clientX: number) => {
      const viewport = getViewport()
      const track = trackRef.current

      if (!viewport || !track) {
        return
      }

      const nextMaxScrollLeft = Math.max(
        viewport.scrollWidth - viewport.clientWidth,
        0
      )

      if (nextMaxScrollLeft <= 0) {
        return
      }

      const trackRect = track.getBoundingClientRect()
      const thumbWidth = Math.max(
        (getThumbWidthPercent(viewport.clientWidth, viewport.scrollWidth) /
          100) *
          trackRect.width,
        MIN_THUMB_WIDTH_PX
      )
      const maxThumbTravel = Math.max(trackRect.width - thumbWidth, 1)
      const nextThumbLeft = clamp(
        clientX - trackRect.left - thumbWidth / 2,
        0,
        maxThumbTravel
      )

      viewport.scrollLeft = (nextThumbLeft / maxThumbTravel) * nextMaxScrollLeft
    },
    [getViewport]
  )

  const handleTrackPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    scrollViewportToTrackPosition(event.clientX)
  }

  const handleThumbPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    const viewport = getViewport()
    const track = trackRef.current

    if (!viewport || !track) {
      return
    }

    const nextMaxScrollLeft = Math.max(
      viewport.scrollWidth - viewport.clientWidth,
      0
    )
    const thumbWidth = Math.max(
      (getThumbWidthPercent(viewport.clientWidth, viewport.scrollWidth) / 100) *
        track.clientWidth,
      MIN_THUMB_WIDTH_PX
    )

    dragStateRef.current = {
      maxScrollLeft: nextMaxScrollLeft,
      maxThumbTravel: Math.max(track.clientWidth - thumbWidth, 1),
      pointerId: event.pointerId,
      startScrollLeft: viewport.scrollLeft,
      startX: event.clientX,
    }

    event.currentTarget.setPointerCapture(event.pointerId)
    event.preventDefault()
    event.stopPropagation()
  }

  const handleThumbPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const dragState = dragStateRef.current
    const viewport = getViewport()

    if (!dragState || dragState.pointerId !== event.pointerId || !viewport) {
      return
    }

    const scrollDelta =
      ((event.clientX - dragState.startX) / dragState.maxThumbTravel) *
      dragState.maxScrollLeft
    viewport.scrollLeft = clamp(
      dragState.startScrollLeft + scrollDelta,
      0,
      dragState.maxScrollLeft
    )
  }

  const handleThumbPointerEnd = (event: PointerEvent<HTMLDivElement>) => {
    if (dragStateRef.current?.pointerId === event.pointerId) {
      dragStateRef.current = null
    }
  }

  const handleThumbKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const viewport = getViewport()

    if (!viewport) {
      return
    }

    const largeStep = viewport.clientWidth
    const smallStep = 80
    const step = event.shiftKey ? largeStep : smallStep

    switch (event.key) {
      case 'ArrowLeft':
        viewport.scrollLeft -= step
        event.preventDefault()
        break
      case 'ArrowRight':
        viewport.scrollLeft += step
        event.preventDefault()
        break
      case 'Home':
        viewport.scrollLeft = 0
        event.preventDefault()
        break
      case 'End':
        viewport.scrollLeft = viewport.scrollWidth
        event.preventDefault()
        break
      default:
        break
    }
  }

  return (
    <SyncedHorizontalScrollbar
      $isPinned={isPinned}
      $isVisible={isVisible}
      aria-hidden={!isVisible}
      aria-label={t`Horizontal table scroll`}>
      <SyncedHorizontalScrollbarTrack
        ref={trackRef}
        onPointerDown={handleTrackPointerDown}>
        <SyncedHorizontalScrollbarThumb
          role='scrollbar'
          aria-label={t`Horizontal table scroll`}
          aria-orientation='horizontal'
          aria-valuemax={Math.round(maxScrollLeft)}
          aria-valuemin={0}
          aria-valuenow={Math.round(metrics.scrollLeft)}
          tabIndex={isVisible ? 0 : -1}
          style={{
            left: `${thumbLeftPercent}%`,
            width: `${thumbWidthPercent}%`,
          }}
          onKeyDown={handleThumbKeyDown}
          onPointerCancel={handleThumbPointerEnd}
          onPointerDown={handleThumbPointerDown}
          onPointerMove={handleThumbPointerMove}
          onPointerUp={handleThumbPointerEnd}
        />
      </SyncedHorizontalScrollbarTrack>
    </SyncedHorizontalScrollbar>
  )
}
